import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { challengeIntro, challengeStats, challengeConclusion } from '../data/challengeContent';
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.6,
    ease: EASE
  }
};
const DIAGRAM_FR = 'https://cdn.magicpatterns.com/uploads/bx3ZdZCThv7wEaQzvLYgyi/ChatGPT_Image_19_sept._2026,_14_59_16.png';
const DIAGRAM_EN = 'https://cdn.magicpatterns.com/uploads/rijxpG1Eb5oQA8H2v64YQ5/ChatGPT_Image_19_sept._2026,_15_03_27.png';
export function Problem() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: {
    en: string;
    fr: string;
  }) => isFR ? value.fr : value.en;
  return <section className="bg-white border-t border-[#E4E6EC] pt-10 lg:pt-12 pb-10 lg:pb-12 px-4 sm:px-6" data-id="element-5649" data-mp="1it54fj0oykq5">
      <div className="max-w-6xl mx-auto" data-id="element-5650" data-mp="1ns7vfzju6qf4">
        {/* Heading */}
        <motion.div {...reveal} className="max-w-3xl" data-id="element-5651" data-mp="t8lz57s0r2c9">
          <p className="font-sans text-xs font-semibold tracking-[0.22em] text-[#4F46E5] uppercase" data-id="element-5652" data-mp="ob40ca7pu3hz">
            {pick(challengeIntro.eyebrow)}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] leading-[1.1] tracking-tight mt-3" data-id="element-5653" data-mp="129tylt69u97x">
            <span className="text-[#121212]" data-id="element-5654" data-mp="x9fnu3dacl67">
              {pick(challengeIntro.headlineLead)}
            </span>{' '}
            <span className="text-[#4F46E5]" data-id="element-5655" data-mp="5kcl4s11rivo">
              {pick(challengeIntro.headlineAccent)}
            </span>
          </h2>
          <p className="font-sans text-base text-[#5B6478] leading-relaxed mt-3" data-id="element-5656" data-mp="jc0s6gwfvh5s">
            {pick(challengeIntro.intro)}
          </p>
        </motion.div>

        {/* Fragmentation diagram + evidence */}
        <div className="grid grid-cols-1 lg:grid-cols-[56fr_44fr] gap-6 lg:gap-8 mt-8 items-center" data-id="element-5657" data-mp="3mzssiyqhehrj">
          <motion.img {...reveal} transition={{
          duration: 0.6,
          delay: 0.05,
          ease: EASE
        }} src={isFR ? DIAGRAM_FR : DIAGRAM_EN} alt={isFR ? 'Points d’entrée — site web, application mobile, courriel, téléphone, réseaux sociaux — reliés aux systèmes CRM, ERP, formulaires, permis, paiements et autres systèmes uniquement par du travail humain : ressaisir, copier-coller, vérifier, rechercher, réconcilier, relancer. Résultat : données fragmentées, doublons, erreurs et coûts qui se multiplient.' : 'Entry points — website, mobile app, email, phone, social media — connected to CRM, ERP, forms, permits, payments and other systems only through human work: re-enter data, copy and paste, verify, search, reconcile, follow up. The result: fragmented data, duplicates, errors and costs that multiply.'} className="block w-full h-auto" data-id="element-5658" data-mp="3i0pzkcns01zx" />

          <div className="flex flex-col gap-3" data-id="element-5659" data-mp="2seov9q2ky32s">
            {challengeStats.map((stat, index) => <motion.article key={stat.id} {...reveal} transition={{
            duration: 0.6,
            delay: 0.1 + index * 0.06,
            ease: EASE
          }} className="rounded-xl border border-[#E4E6EC] bg-white shadow-[0_1px_2px_rgba(18,18,18,0.04)] px-5 py-4" data-id="element-5660" data-mp="gr3hxm33t256" data-mpi={index}>
                <div className="flex items-baseline gap-4" data-id="element-5661" data-mp="30gjzw563ga5">
                  <span className="font-serif text-3xl text-[#4F46E5] leading-none shrink-0" data-id="element-5662" data-mp="2xuavs2mospt">
                    {pick(stat.value)}
                  </span>
                  <p className="font-sans text-sm text-[#121212]/70 leading-snug" data-id="element-5663" data-mp="3p63l2h3z0pv4">
                    {pick(stat.claim)}
                  </p>
                </div>
                <a href={stat.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-block font-sans text-xs text-[#5B6478] hover:text-[#4F46E5] underline decoration-transparent hover:decoration-current underline-offset-2 transition-colors duration-200 mt-2.5" data-id="element-5664" data-mp="3g6qb7nbxpz9v">
                  {pick(stat.source)}
                </a>
              </motion.article>)}
          </div>
        </div>

        {/* Conclusion banner */}
        <motion.div {...reveal} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: EASE
      }} className="mt-6 rounded-xl bg-[#4F46E5]/[0.04] px-6 py-5 lg:px-8 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-8" data-id="element-5665" data-mp="22gfyccp8vhr">
          <p className="font-serif text-xl lg:text-2xl leading-snug flex-1" data-id="element-5666" data-mp="3oh9idr35s7ra">
            <span className="text-[#121212]" data-id="element-5667" data-mp="3tb40wr712gos">
              {pick(challengeConclusion.lead)}
            </span>{' '}
            <span className="text-[#4F46E5]" data-id="element-5668" data-mp="3fgl6b1xbshh1">
              {pick(challengeConclusion.accent)}
            </span>
          </p>

          <span className="hidden lg:block w-px self-stretch bg-[#4F46E5]/15" aria-hidden="true" data-id="element-5669" data-mp="35jouonc4rf89" />

          <p className="font-sans text-sm text-[#5B6478] leading-snug lg:max-w-[16rem]" data-id="element-5670" data-mp="3ai06h3lzjzt4">
            {pick(challengeConclusion.caption)}
          </p>
        </motion.div>
      </div>
    </section>;
}